import { DateTime } from 'luxon';
import {
    RegimeModel,
    RegimePeriodModel,
    ScheduleTeam,
} from 'app/models/schedule.model';
import { formatTimeRange, parseTimeToMinutes, regimeWindows } from '../../shared/regime-time.utils';

export const PRESENCE_ROLE_ORDER = ['DRIVER', 'NURSE', 'PARAMEDIC', 'DOCTOR', 'OTHER'] as const;
export const MINUTES_PER_PRESENCE_DAY = 1440;

/** Cor de identificação visual por função, usada nas barras do Gantt vertical e na legenda. */
export const PRESENCE_ROLE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
    DRIVER: { bg: '#dbeafe', border: '#60a5fa', text: '#1d4ed8' },
    NURSE: { bg: '#dcfce7', border: '#4ade80', text: '#15803d' },
    PARAMEDIC: { bg: '#fef3c7', border: '#fbbf24', text: '#b45309' },
    DOCTOR: { bg: '#ede9fe', border: '#a78bfa', text: '#6d28d9' },
    OTHER: { bg: '#e2e8f0', border: '#94a3b8', text: '#334155' },
};

export interface PresenceTimeInterval {
    start: number;
    end: number;
}

export interface RequiredRoleSlot {
    role: string;
    regimeId: number;
    regimeSlot: number;
    interval: PresenceTimeInterval;
    periodLabel: string;
}

export interface SpanPositioned {
    startPct: number;
    sizePct: number;
    lane: number;
    laneCount: number;
}

export function prevPresenceDateKey(dateKey: string): string {
    return DateTime.fromISO(dateKey).minus({ days: 1 }).toFormat('yyyy-MM-dd');
}

export function presenceIntervalMinutes(start: number, end: number): number {
    return end >= start ? end - start : end + MINUTES_PER_PRESENCE_DAY - start;
}

export function segmentOnPresenceDay(interval: PresenceTimeInterval): PresenceTimeInterval | null {
    if (interval.start >= MINUTES_PER_PRESENCE_DAY) return null;
    const end = Math.min(interval.end, MINUTES_PER_PRESENCE_DAY);
    if (end <= interval.start) return null;
    return { start: interval.start, end };
}

export function carryoverToNextPresenceDay(interval: PresenceTimeInterval): PresenceTimeInterval | null {
    if (interval.end <= MINUTES_PER_PRESENCE_DAY) return null;
    return { start: 0, end: interval.end - MINUTES_PER_PRESENCE_DAY };
}

export function intervalToSpanPct(interval: PresenceTimeInterval): { startPct: number; sizePct: number } {
    return {
        startPct: (interval.start / MINUTES_PER_PRESENCE_DAY) * 100,
        sizePct: (presenceIntervalMinutes(interval.start, interval.end) / MINUTES_PER_PRESENCE_DAY) * 100,
    };
}

export function periodsForRegime(regime: RegimeModel): RegimePeriodModel[] {
    const persisted = (regime.periods ?? []).filter((p) => p.regime_period_id != null);
    if (persisted.length) return persisted;
    return regimeWindows(regime).map((w) => ({
        regime_id: regime.regime_id,
        slot: w.slot,
        name: w.label || null,
        start_time: w.start_time,
        end_time: w.end_time,
        min_staff: 0,
    }));
}

export function periodInterval(period: RegimePeriodModel): PresenceTimeInterval | null {
    if (!period.start_time || !period.end_time) return null;
    let start = parseTimeToMinutes(period.start_time);
    let end = parseTimeToMinutes(period.end_time);
    if (end <= start) end += MINUTES_PER_PRESENCE_DAY;
    return { start, end };
}

function resolveRegimeIdForRole(role: string, team: ScheduleTeam | undefined): number | null {
    const member = team?.members?.find((m) => (m.role || 'OTHER') === role);
    return member?.regime_id ?? null;
}

export function buildRequiredRoleSlots(team: ScheduleTeam | undefined, regimes: RegimeModel[]): RequiredRoleSlot[] {
    if (!team?.members?.length) return [];

    const regimeById = new Map((regimes || []).filter((r) => r.regime_id != null).map((r) => [r.regime_id!, r]));
    const rolesInTeam = new Set(team.members.map((m) => m.role || 'OTHER'));
    const seen = new Set<string>();
    const slots: RequiredRoleSlot[] = [];

    const pushRole = (role: string): void => {
        const regimeId = resolveRegimeIdForRole(role, team);
        if (regimeId == null) return;
        const regime = regimeById.get(regimeId);
        if (!regime) return;

        for (const period of periodsForRegime(regime)) {
            const interval = periodInterval(period);
            if (!interval) continue;
            const slot = period.slot ?? 0;
            const dedupeKey = `${role}|${regimeId}|${slot}`;
            if (seen.has(dedupeKey)) continue;
            seen.add(dedupeKey);

            const range = formatTimeRange(period.start_time, period.end_time);
            const periodLabel = [period.name || period.sigla || `Turno ${slot + 1}`, range].filter(Boolean).join(' · ');
            slots.push({ role, regimeId, regimeSlot: slot, interval, periodLabel });
        }
    };

    for (const role of PRESENCE_ROLE_ORDER) {
        if (rolesInTeam.has(role)) pushRole(role);
    }
    for (const role of rolesInTeam) {
        if ((PRESENCE_ROLE_ORDER as readonly string[]).includes(role)) continue;
        pushRole(role);
    }

    return slots;
}

export function buildRoleLaneMap(team: ScheduleTeam | undefined, requiredSlots: RequiredRoleSlot[]): Map<string, number> {
    const roles = new Set<string>();
    for (const m of team?.members ?? []) roles.add(m.role || 'OTHER');
    for (const slot of requiredSlots) roles.add(slot.role);

    const map = new Map<string, number>();
    let idx = 0;
    for (const role of PRESENCE_ROLE_ORDER) {
        if (!roles.has(role)) continue;
        map.set(role, idx++);
    }
    for (const role of roles) {
        if (map.has(role)) continue;
        map.set(role, idx++);
    }
    return map;
}

export function spanOverlap(a: SpanPositioned, b: SpanPositioned): boolean {
    return a.startPct < b.startPct + b.sizePct - 0.05 && b.startPct < a.startPct + a.sizePct - 0.05;
}

function assignSpanLanes(items: SpanPositioned[]): void {
    const sorted = [...items].sort((a, b) => a.startPct - b.startPct || a.sizePct - b.sizePct);
    const lanes: { end: number }[] = [];

    for (const item of sorted) {
        const itemEnd = item.startPct + item.sizePct;
        let lane = lanes.findIndex((l) => l.end <= item.startPct + 0.05);
        if (lane < 0) {
            lane = lanes.length;
            lanes.push({ end: itemEnd });
        } else {
            lanes[lane].end = itemEnd;
        }
        item.lane = lane;
    }

    const laneCount = Math.max(1, lanes.length);
    for (const item of sorted) item.laneCount = laneCount;
}

export function assignRoleBasedSpanLanes(
    blocks: SpanPositioned[],
    placeholders: SpanPositioned[],
    roleLaneMap: Map<string, number>,
    roleOf: (item: SpanPositioned) => string,
): number {
    const roles = [...roleLaneMap.keys()].sort((a, b) => (roleLaneMap.get(a) ?? 0) - (roleLaneMap.get(b) ?? 0));
    let offset = 0;

    for (const role of roles) {
        const roleBlocks = blocks.filter((b) => roleOf(b) === role);
        const rolePlaceholders = placeholders.filter((p) => roleOf(p) === role);
        const combined = [...rolePlaceholders, ...roleBlocks];
        if (!combined.length) continue;

        assignSpanLanes(combined);
        const subLanes = Math.max(...combined.map((c) => c.laneCount));
        for (const item of combined) item.lane = offset + item.lane;
        offset += subLanes;
    }

    const laneCount = Math.max(1, offset);
    for (const item of [...blocks, ...placeholders]) item.laneCount = laneCount;
    return laneCount;
}

export function spanCoversInterval(item: SpanPositioned, startPct: number, sizePct: number): boolean {
    return item.startPct <= startPct + 0.05 && item.startPct + item.sizePct >= startPct + sizePct - 0.05;
}

export function isSpanLargeEnough(sizePct: number): boolean {
    return sizePct >= 0.15;
}
